import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { TruncatedInputLabel } from 'styles/InputStyles';

import { FormControl } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { CalendarIcon, DatePicker, PickersLayout } from '@mui/x-date-pickers';

const StyledPickersLayout = styled(PickersLayout)({
  '.MuiPickersMonth-monthButton': {
    borderRadius: '5px',
  },
  '.MuiPickersYear-yearButton': {
    borderRadius: '5px',
  },
  '.MuiYearCalendar-root': {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  },
});

interface ICustomDatePickerProps {
  label: string;
  name: string;
  setValue: (value: Dayjs | null) => void;
  value?: Dayjs | null;
  error?: boolean;
  format?: string;
  views?: import('@mui/x-date-pickers').DateView[];
  minDate?: Dayjs;
  maxDate?: Dayjs;
  helperText?: string;
  required?: boolean;
}

const CustomDatePicker: React.FC<ICustomDatePickerProps> = ({
  label,
  name,
  setValue,
  value,
  error,
  format,
  views,
  minDate,
  maxDate,
  helperText,
  required,
}) => {
  const [open, setOpen] = useState(false);
  const parsedValue = dayjs(value);

  const spanRef = useRef<HTMLLabelElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (spanRef.current) {
        setIsTruncated(
          spanRef.current.scrollWidth > spanRef.current.clientWidth,
        );
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [label]);

  return (
    <FormControl fullWidth variant="filled">
      <Tooltip
        title={isTruncated ? label : ''}
        arrow
        disableHoverListener={!isTruncated}
        placement="top"
      >
        <TruncatedInputLabel>
          <span ref={spanRef} className="label-text">
            {label}
          </span>
          {required && <span className="required-asterisk">*</span>}
        </TruncatedInputLabel>
      </Tooltip>

      <DatePicker
        name={name}
        open={open}
        onClose={() => setOpen(false)}
        format={format ?? 'DD/MM/YYYY'}
        views={views}
        minDate={minDate}
        maxDate={maxDate}
        value={parsedValue.isValid() ? parsedValue : null}
        onChange={(newDate) => (!!newDate ? setValue(newDate) : null)}
        data-testid={`datepicker-${name}`}
        slotProps={{
          textField: {
            size: 'small',
            variant: 'filled',
            hiddenLabel: true,
            onClick: () => setOpen(true),
            InputProps: {
              endAdornment: <CalendarIcon sx={{ fontSize: 20 }} />,
              style: {
                fontSize: '0.9em',
                height: '40px',
              },
            },
            required: required,
            fullWidth: true,
            error: error,
            helperText: helperText,
          },
        }}
        slots={{ layout: StyledPickersLayout }}
      />
    </FormControl>
  );
};

export default CustomDatePicker;
